import * as THREE from "three";
import { JOINT_SPHERE_SIZES, JOINT_SPHERE_SEGMENTS, JOINT_MATERIALS } from "./jointStyles";
import { getScalarJointRelativeValue } from "./scalarJointValue";

const HINGE_ARC_SEGMENTS = 32;
const HINGE_SEMICIRCLE_EPSILON = 1e-5;
const HINGE_BASIS_EPSILON = 1e-8;

function getVectorFromArray(value) {
  if (!Array.isArray(value) || value.length !== 3) return null;
  const vector = new THREE.Vector3(Number(value[0]), Number(value[1]), Number(value[2]));
  return Number.isFinite(vector.x) && Number.isFinite(vector.y) && Number.isFinite(vector.z)
    ? vector
    : null;
}

function createFallbackHingeBasis(axis) {
  let perp1;
  if (Math.abs(axis.x) < 0.9) {
    perp1 = new THREE.Vector3(1, 0, 0);
  } else if (Math.abs(axis.y) < 0.9) {
    perp1 = new THREE.Vector3(0, 1, 0);
  } else {
    perp1 = new THREE.Vector3(0, 0, 1);
  }

  perp1.sub(axis.clone().multiplyScalar(perp1.dot(axis))).normalize();
  const perp2 = new THREE.Vector3().crossVectors(axis, perp1).normalize();

  const rotationMatrix = new THREE.Matrix4();
  rotationMatrix.makeRotationAxis(axis, Math.PI / 2);
  perp1.applyMatrix4(rotationMatrix);
  perp2.applyMatrix4(rotationMatrix);

  return { perp1, perp2 };
}

function createHingeArcBasis(axis, basisHint) {
  const hint = getVectorFromArray(basisHint);
  if (hint) {
    const projectedHint = hint.sub(axis.clone().multiplyScalar(hint.dot(axis)));
    if (projectedHint.lengthSq() > HINGE_BASIS_EPSILON) {
      const perp1 = projectedHint.normalize();
      const perp2 = new THREE.Vector3().crossVectors(axis, perp1).normalize();
      return { perp1, perp2 };
    }
  }

  return createFallbackHingeBasis(axis);
}

function getHingeArcPoint(perp1, perp2, angle, radius) {
  return perp1
    .clone()
    .multiplyScalar(Math.cos(angle) * radius)
    .add(perp2.clone().multiplyScalar(Math.sin(angle) * radius));
}

function getHingeArcSpan(minAngle, maxAngle) {
  const span = maxAngle - minAngle;
  if (isHingeSemicircle(minAngle, maxAngle)) {
    return span >= 0 ? -Math.PI : Math.PI;
  }
  return span;
}

function isHingeSemicircle(minAngle, maxAngle) {
  return Math.abs(Math.abs(maxAngle - minAngle) - Math.PI) < HINGE_SEMICIRCLE_EPSILON;
}

function createHingeArcPoints(perp1, perp2, minAngle, maxAngle, radius) {
  const arcAngle = getHingeArcSpan(minAngle, maxAngle);
  const points = [];
  for (let i = 0; i <= HINGE_ARC_SEGMENTS; i++) {
    points.push(
      getHingeArcPoint(perp1, perp2, minAngle + (arcAngle * i) / HINGE_ARC_SEGMENTS, radius)
    );
  }
  return points;
}

/**
 * Creates visualization elements for a hinge joint
 * @param {Object} jointInfo - Joint information object
 * @param {string} jointInfo.name - Joint name
 * @param {Array} jointInfo.axis - Joint axis [x, y, z]
 * @param {Array} jointInfo.range - Joint range [min, max]
 * @param {THREE.Quaternion} parentRot - Parent rotation
 * @param {boolean} showJoints - Whether to show joint visualizations
 * @param {Object} jointMarkerSizes - Joint marker sizes object
 * @returns {THREE.Group} Group containing all hinge joint visualization elements
 */
export function createHingeJointVisualization(
  jointInfo,
  parentRot,
  showJoints,
  jointMarkerSizes,
  visualizationOptions = null
) {
  const { name: jointName, axis: jointAxis, uuid: jointUuid } = jointInfo;
  const jointRange =
    Array.isArray(jointInfo.range) && jointInfo.range.length === 2
      ? jointInfo.range
      : [-Math.PI, Math.PI];
  const toVisualAngle = (value) => getScalarJointRelativeValue(jointInfo, value);

  // Create a group for all joint visualization elements
  const jointVizGroup = new THREE.Group();
  jointVizGroup.userData.isJointVizGroup = true;
  jointVizGroup.userData.jointName = jointName;
  jointVizGroup.userData.jointUuid = jointUuid;
  jointVizGroup.userData.jointType = "hinge";

  // Use dynamic arc radius based on marker size (keep relative to center size)
  const arcRadius = jointMarkerSizes ? jointMarkerSizes.center * 3 : JOINT_SPHERE_SIZES.center * 3;

  // Use joint axis directly in body-local coordinates
  // The visualization group is added to the body's poseGroup, which is in body-local space
  // So we should NOT transform the axis to world space - use it as-is
  const axis = new THREE.Vector3(...(Array.isArray(jointAxis) ? jointAxis : [0, 0, 1])).normalize();

  // Create arc geometry in the plane perpendicular to the joint axis
  const arcGeometry = new THREE.BufferGeometry();
  const [minA, maxA] = jointRange.map(toVisualAngle);

  // Find two perpendicular vectors to create the arc plane
  const axisNormalized = axis.clone().normalize();
  const basisHint = isHingeSemicircle(minA, maxA) ? null : visualizationOptions?.basisHint;
  let { perp1, perp2 } = createHingeArcBasis(axisNormalized, basisHint);

  // Create arc points in the plane defined by perp1 and perp2
  arcGeometry.setFromPoints(createHingeArcPoints(perp1, perp2, minA, maxA, arcRadius));

  // Apply rotation to align with world axis
  const arcMaterial = new THREE.LineBasicMaterial(JOINT_MATERIALS.arc);
  const arc = new THREE.Line(arcGeometry, arcMaterial);
  arc.userData.isJointArc = true;
  arc.renderOrder = 1; // Render first (behind other elements)
  arc.userData.updateArc = (min, max) => {
    const visualMin = toVisualAngle(min);
    const visualMax = toVisualAngle(max);
    arc.geometry.setFromPoints(createHingeArcPoints(perp1, perp2, visualMin, visualMax, arcRadius));
  };

  arc.userData.jointUuid = jointUuid;
  jointVizGroup.add(arc);

  // Create min position marker (teal sphere)
  const minSphereGeometry = new THREE.SphereGeometry(
    jointMarkerSizes ? jointMarkerSizes.endpoint : JOINT_SPHERE_SIZES.endpoint,
    JOINT_SPHERE_SEGMENTS.endpoint,
    JOINT_SPHERE_SEGMENTS.endpoint
  );
  const minSphereMaterial = new THREE.MeshBasicMaterial({
    ...JOINT_MATERIALS.minEndpoint,
    side: THREE.DoubleSide,
  });
  const minSphere = new THREE.Mesh(minSphereGeometry, minSphereMaterial);
  const minPos = getHingeArcPoint(perp1, perp2, minA, arcRadius);
  minSphere.position.copy(minPos);
  minSphere.userData.isJointEndpoint = true;
  minSphere.userData.jointUuid = jointUuid;
  minSphere.userData.jointName = jointName;
  minSphere.renderOrder = 2; // Render after arc but before center sphere
  minSphere.userData.updatePosition = (min) => {
    const visualMin = toVisualAngle(min);
    const newMinPos = getHingeArcPoint(perp1, perp2, visualMin, arcRadius);
    minSphere.position.copy(newMinPos);
  };
  jointVizGroup.add(minSphere);

  // Create max position marker (red sphere)
  const maxSphereGeometry = new THREE.SphereGeometry(
    jointMarkerSizes ? jointMarkerSizes.endpoint : JOINT_SPHERE_SIZES.endpoint,
    JOINT_SPHERE_SEGMENTS.endpoint,
    JOINT_SPHERE_SEGMENTS.endpoint
  );
  const maxSphereMaterial = new THREE.MeshBasicMaterial({
    ...JOINT_MATERIALS.maxEndpoint,
    side: THREE.DoubleSide,
  });
  const maxSphere = new THREE.Mesh(maxSphereGeometry, maxSphereMaterial);
  const maxPos = getHingeArcPoint(perp1, perp2, maxA, arcRadius);
  maxSphere.position.copy(maxPos);
  maxSphere.userData.isJointEndpoint = true;
  maxSphere.userData.jointUuid = jointUuid;
  maxSphere.userData.jointName = jointName;
  maxSphere.renderOrder = 2; // Render after arc but before center sphere
  maxSphere.userData.updatePosition = (max) => {
    const visualMax = toVisualAngle(max);
    const newMaxPos = getHingeArcPoint(perp1, perp2, visualMax, arcRadius);
    maxSphere.position.copy(newMaxPos);
  };
  jointVizGroup.add(maxSphere);

  // Create center marker (green sphere)
  const centerSphereGeometry = new THREE.SphereGeometry(
    jointMarkerSizes ? jointMarkerSizes.center : JOINT_SPHERE_SIZES.center,
    JOINT_SPHERE_SEGMENTS.center,
    JOINT_SPHERE_SEGMENTS.center
  );
  const centerSphereMaterial = new THREE.MeshBasicMaterial({
    ...JOINT_MATERIALS.center,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 1,
    depthTest: false,
    depthWrite: false,
  });
  const centerSphere = new THREE.Mesh(centerSphereGeometry, centerSphereMaterial);
  centerSphere.userData.jointName = jointName;
  centerSphere.userData.jointUuid = jointUuid;
  centerSphere.renderOrder = 10000;
  jointVizGroup.add(centerSphere);

  // Store references for updates
  jointVizGroup.userData.arc = arc;
  jointVizGroup.userData.minSphere = minSphere;
  jointVizGroup.userData.maxSphere = maxSphere;
  jointVizGroup.userData.centerSphere = centerSphere;
  jointVizGroup.userData.perp1 = perp1;
  jointVizGroup.userData.perp2 = perp2;
  jointVizGroup.userData.arcRadius = arcRadius;
  jointVizGroup.userData.parentRot = parentRot ? parentRot.clone() : new THREE.Quaternion();
  jointVizGroup.userData.localAxis = axis.clone(); // Store local axis for updates

  // Update function for range and axis changes
  jointVizGroup.userData.updateAll = (min, max, axisLocal) => {
    const visualMin = toVisualAngle(min);
    const visualMax = toVisualAngle(max);
    // If a new axis is provided, rebuild arc plane about the new axis
    if (Array.isArray(axisLocal) && axisLocal.length === 3) {
      // Use axis directly in body-local coordinates (no world transformation needed)
      const axisNorm = new THREE.Vector3(...axisLocal).normalize();
      jointVizGroup.userData.localAxis = axisNorm.clone();

      const nextBasisHint = isHingeSemicircle(visualMin, visualMax)
        ? null
        : visualizationOptions?.basisHint;
      ({ perp1, perp2 } = createHingeArcBasis(axisNorm, nextBasisHint));

      // Rebuild arc geometry points for new axis and given range
      arc.geometry.setFromPoints(
        createHingeArcPoints(perp1, perp2, visualMin, visualMax, arcRadius)
      );

      // Update endpoints for the new arc basis
      const newMinPos = getHingeArcPoint(perp1, perp2, visualMin, arcRadius);
      const newMaxPos = getHingeArcPoint(perp1, perp2, visualMax, arcRadius);
      minSphere.position.copy(newMinPos);
      maxSphere.position.copy(newMaxPos);

      // Persist updated basis on userData
      jointVizGroup.userData.perp1 = perp1;
      jointVizGroup.userData.perp2 = perp2;
      return;
    }

    // No axis change: update points along existing basis
    if (arc.userData.updateArc) {
      arc.userData.updateArc(min, max);
    }
    if (minSphere.userData.updatePosition) {
      minSphere.userData.updatePosition(min);
    }
    if (maxSphere.userData.updatePosition) {
      maxSphere.userData.updatePosition(max);
    }
  };

  // Initial visibility is controlled by Scene effect via store; default to showJoints until that runs
  jointVizGroup.visible = !!showJoints;
  arc.visible = !!showJoints;
  minSphere.visible = !!showJoints;
  maxSphere.visible = !!showJoints;
  centerSphere.visible = !!showJoints;

  return jointVizGroup;
}

// Ensure pivot group exists and is positioned for current joint.position (body-local space)
function ensurePivotForHinge(bodyGroup, initialPos, initialRot, jointPosLocal) {
  const parent = bodyGroup.parent;
  if (!parent) return null;
  if (!bodyGroup.userData._pivotGroup) {
    const pivot = new THREE.Group();
    pivot.userData.isJointPivot = true;
    parent.add(pivot);
    pivot.add(bodyGroup);
    bodyGroup.userData._pivotGroup = pivot;
  }
  const pivot = bodyGroup.userData._pivotGroup;
  const jointPos = new THREE.Vector3().fromArray(jointPosLocal || [0, 0, 0]);
  const cached = bodyGroup.userData._pivotLocal || [NaN, NaN, NaN];
  if (cached[0] !== jointPos.x || cached[1] !== jointPos.y || cached[2] !== jointPos.z) {
    // Transform joint position from body-local to parent space
    // NOTE: bodyRot is initial rotation of this body relative to parent.
    // IMPORTANT: do not use the current bodyGroup quaternion here. When physics has been
    // running, bodyGroup.quaternion can be a physics pose, which would rebuild pivots in
    // the wrong place when switching back to kinematics.
    const bodyRot = initialRot || bodyGroup.quaternion;
    const jointOffsetParent = jointPos.clone().applyQuaternion(bodyRot);
    const jointPosInParent = jointOffsetParent.clone().add(initialPos);

    // Pivot is at joint position in parent space
    pivot.position.copy(jointPosInParent);

    // Body is positioned relative to pivot so that q = 0 reproduces the
    // original pose exactly, even when the body has a non-identity rotation.
    // Derivation: with pivot at R_B * p_B + t_B, body at -R_B * p_B (in parent
    // space) and rotation R_B, the composed transform gives back (R_B, t_B).
    bodyGroup.position.copy(jointOffsetParent.clone().negate());

    bodyGroup.userData._pivotLocal = [jointPos.x, jointPos.y, jointPos.z];
  }
  return pivot;
}

/**
 * Applies a hinge joint value to a body group
 */
export function applyHingeJointValue(bodyGroup, joint, value, initialRot, initialPos) {
  const appliedValue = getScalarJointRelativeValue(joint, value);
  // Transform joint axis to parent-local coordinates (not world space!)
  // The pivot is in the parent's coordinate frame, so the axis must be in parent-local space
  // Joint axis is in body-local coordinates, so transform by body's quaternion (initialRot)
  const axis = new THREE.Vector3(...(Array.isArray(joint.axis) ? joint.axis : [0, 0, 1]))
    .normalize()
    .applyQuaternion(initialRot) // Body-local -> parent-local (pivot's coordinate frame)
    .normalize();

  if (Math.abs(appliedValue) < 1e-6) {
    // Reset to initial state - remove any existing pivot
    if (bodyGroup.userData._pivotGroup) {
      const pivot = bodyGroup.userData._pivotGroup;
      const parent = pivot.parent;
      if (parent) {
        // Move body back to parent and restore original position
        parent.add(bodyGroup);
        bodyGroup.position.copy(initialPos);
        bodyGroup.quaternion.copy(initialRot);
        // Remove pivot from scene
        parent.remove(pivot);
      }
      delete bodyGroup.userData._pivotGroup;
      delete bodyGroup.userData._pivotLocal;
    } else {
      // No pivot exists, just ensure correct positioning
      bodyGroup.position.copy(initialPos);
      bodyGroup.quaternion.copy(initialRot);
    }
    return;
  }

  // For non-zero values, create/use pivot system
  const pivot = ensurePivotForHinge(bodyGroup, initialPos, initialRot, joint.position || [0, 0, 0]);

  // Child keeps its initial local rotation
  bodyGroup.quaternion.copy(initialRot);

  // Rotate the pivot group around the axis at the pivot
  if (pivot) {
    pivot.quaternion.setFromAxisAngle(axis, appliedValue);
  } else {
    // Fallback (no pivot), original behavior
    const rotation = new THREE.Quaternion().setFromAxisAngle(axis, appliedValue);
    bodyGroup.quaternion.copy(initialRot);
    bodyGroup.quaternion.premultiply(rotation);
    bodyGroup.position.copy(initialPos);
  }
}

/**
 * Updates the visibility of hinge joint visualization elements
 */
export function updateHingeJointVisibility(jointVizGroup, showJoints) {
  if (jointVizGroup.userData.arc) {
    jointVizGroup.userData.arc.visible = showJoints;
  }
  if (jointVizGroup.userData.minSphere) {
    jointVizGroup.userData.minSphere.visible = showJoints;
  }
  if (jointVizGroup.userData.maxSphere) {
    jointVizGroup.userData.maxSphere.visible = showJoints;
  }
}
